import { Pill } from '@/features/dossier/components/primitives'

/**
 * Communications primitives.
 *
 * The rules they carry: an unconfigured channel says so rather than looking ready, a cost is
 * shown in whole shillings because that is how a budget is read, and ACCEPTED never renders as
 * "delivered".
 */

/**
 * A recipient or campaign status.
 *
 * <p>ACCEPTED is amber, not green. It means a provider took the message — which is genuinely
 * progress and genuinely not arrival, and a green tick against it is the single most misleading
 * thing this screen could show.
 */
export function StatusChip({ code }: { code: string }) {
  const tone =
    code === 'DELIVERED' || code === 'SENT'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    : code === 'ACCEPTED' || code === 'SENDING' || code === 'QUEUED'
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400'
    : code === 'FAILED' || code === 'UNDELIVERED'
      ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
    : code === 'SUPPRESSED' || code === 'CANCELLED' || code === 'STOPPED'
      ? 'border-border text-muted-foreground'
    : code === 'SCHEDULED' || code === 'PENDING_APPROVAL'
      ? 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400'
    : 'border-border'
  return <Pill className={tone}>{code.toLowerCase().replace(/_/g, ' ')}</Pill>
}

/** KES, whole shillings. A campaign budget is not read to four decimal places. */
export function Cost({ value, prefix }: { value: string | null | undefined; prefix?: string }) {
  if (value == null) {
    return <span className="text-[11px] text-muted-foreground">—</span>
  }
  return (
    <span className="text-[11px] tabular-nums">
      {prefix}KES {Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}
    </span>
  )
}

/**
 * A channel that cannot send says so, here, before a campaign is built on it.
 *
 * The alternative is finding out at 6am, which is the one time nobody can fix it.
 */
export function ChannelReady({ configured, providerCode }: {
  configured: boolean
  providerCode: string
}) {
  if (configured) {
    return (
      <Pill className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700
                       dark:text-emerald-400">ready</Pill>
    )
  }
  return (
    <Pill className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          >{providerCode} not configured</Pill>
  )
}

/**
 * Parts, and what they cost.
 *
 * An SMS is 160 GSM-7 characters. One emoji or one curly apostrophe pasted from a word
 * processor makes the whole message UCS-2 at 70, and a campaign nobody re-costed becomes three
 * times its estimate — so the number is on the compose screen, not in a report afterwards.
 */
export function Parts({ parts, characters }: { parts: number | null; characters?: number }) {
  if (parts == null) {
    return null
  }
  const tone = parts > 1 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
  return (
    <span className={`text-[10px] ${tone}`}>
      {characters != null && `${characters} characters · `}
      {parts} SMS part{parts === 1 ? '' : 's'} each
      {parts > 1 && ' — every recipient is billed for all of them'}
    </span>
  )
}

/**
 * Reach and the gap in it, side by side.
 *
 * "We messaged the ward" and "we messaged the 62% of the ward whose numbers we hold" are
 * different claims, and only one of them is true. The suppressed count is never hidden.
 */
export function Reach({ recipients, suppressed }: { recipients: number; suppressed: number }) {
  const total = recipients + suppressed
  return (
    <span className="text-[11px]">
      <b className="tabular-nums">{recipients.toLocaleString()}</b> reachable
      {suppressed > 0 && (
        <span className="text-muted-foreground">
          {' '}· {suppressed.toLocaleString()} not contactable
          {total > 0 && ` (${Math.round((recipients / total) * 100)}% of the audience)`}
        </span>
      )}
    </span>
  )
}
